/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  DeleteOutlined,
  DollarOutlined,
  ExportOutlined,
} from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import { paragraphSmall } from 'src/components/css'
import RefundIcon from 'src/components/icons/RefundIcon'
import {
  CANCELLED_STATUS_ID,
  getPaymentDestination,
  getPendingPayments,
  getRegistrationDetailsPath,
  isRegistrationSession,
} from '../../utils/registrationRows'

export interface ActionsCellParams {
  isMobile: boolean
  onResumeSession: (record: UserRegistration) => void
  onDeleteSession: (record: UserRegistration) => void
}

type ActionsCellProps = ICellRendererParams<UserRegistration> &
  ActionsCellParams

const goToPayment = (record: UserRegistration) => {
  const { url, newTab } = getPaymentDestination(record)

  if (newTab) {
    window.open(url, '_blank')
  } else {
    window.location.href = url
  }
}

/** Restore or delete a session; view or pay a submitted registration. */
const ActionsCell = ({
  data: record,
  isMobile,
  onResumeSession,
  onDeleteSession,
}: ActionsCellProps) => {
  if (!record) {
    return null
  }

  const isSession = isRegistrationSession(record)
  const canPay =
    !isSession &&
    record.status_id !== CANCELLED_STATUS_ID &&
    getPendingPayments(record).length > 0

  return (
    <div css={rowStyle}>
      <Tooltip title={isSession ? 'Restore Session' : 'View Details'}>
        {isSession ? (
          <ExportOutlined
            css={restoreStyle}
            onClick={() => onResumeSession(record)}
          />
        ) : (
          <Link to={getRegistrationDetailsPath(record)} css={detailsStyle}>
            <RefundIcon size={24} color={colors.PRIMARY} />
          </Link>
        )}
      </Tooltip>
      {canPay ? (
        <div css={payStyle} onClick={() => goToPayment(record)}>
          <DollarOutlined css={payIconStyle} />
          {isMobile ? null : <span css={payLabelStyle}>Pay</span>}
        </div>
      ) : null}
      {isSession ? (
        <Tooltip title='Delete Session'>
          <DeleteOutlined
            css={deleteStyle}
            onClick={() => onDeleteSession(record)}
          />
        </Tooltip>
      ) : null}
    </div>
  )
}

export default ActionsCell

// ─── Styles ──────────────────────────────────────────────────────

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const restoreStyle = css`
  font-size: 20px;
  color: ${colors.PRIMARY};
  cursor: pointer;
`

const detailsStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const payStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 2px 8px;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  background-color: ${colors.PRIMARY};
  cursor: pointer;
`

const payIconStyle = css`
  font-size: 18px;
  color: ${PRIMARY_BUTTON_CONTENT.icon};
`

const payLabelStyle = css`
  ${paragraphSmall}
  color: ${PRIMARY_BUTTON_CONTENT.text};
`

const deleteStyle = css`
  font-size: 20px;
  color: ${colors.ERROR_200};
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`
